export interface StrongAuthErrorPresentation {
    message: string;
    terminal: boolean;
    retryPasskey?: boolean;
}

export interface StrongAuthErrorContext {
    hasAuthenticatorFallback?: boolean;
}

const serverErrorPresentations: Record<string, StrongAuthErrorPresentation> = {
    TOTP_INVALID_CODE: {
        message: 'That code is incorrect or expired. Check your authenticator app and try again.',
        terminal: false,
    },
    TOTP_CODE_REPLAYED: {
        message: 'That code was already used. Wait for a new code and try again.',
        terminal: false,
    },
    STRONG_AUTH_RATE_LIMITED: {
        message: 'Too many attempts. Wait a minute, then try again.',
        terminal: false,
    },
    STRONG_AUTH_STORAGE_UNAVAILABLE: {
        message: 'The security service is temporarily unavailable. Try again shortly.',
        terminal: false,
    },
    STRONG_AUTH_EXPIRED: {
        message: 'This security check expired. Start again from the sign-in page.',
        terminal: true,
    },
};

export function getStrongAuthErrorPresentation(
    error: unknown,
    context: StrongAuthErrorContext = {}
): StrongAuthErrorPresentation {
    const browserName = getStringProperty(error, 'name');
    if (browserName === 'NotAllowedError') {
        return {
            message: context.hasAuthenticatorFallback
                ? 'Passkey wasn’t completed. Try again or use your authenticator app.'
                : 'Passkey wasn’t completed. Try the passkey again.',
            terminal: false,
            retryPasskey: true,
        };
    }
    if (browserName === 'InvalidStateError') {
        return {
            message: 'That passkey is already registered. Try another passkey or use an authenticator app.',
            terminal: false,
        };
    }
    if (browserName === 'NotSupportedError' || browserName === 'SecurityError') {
        return {
            message: context.hasAuthenticatorFallback
                ? 'This device can’t use a passkey here. Use an authenticator app instead.'
                : 'Passkeys aren’t available in this browser. Open this page in Chrome, Safari, or Edge.',
            terminal: false,
        };
    }

    const response = error as {
        status?: number;
        error?: { errorCode?: string };
    };
    const code = response?.error?.errorCode;
    if (code === 'PASSKEY_VERIFICATION_FAILED') {
        return {
            message: context.hasAuthenticatorFallback
                ? 'That passkey could not be verified. Try again or use your authenticator app.'
                : 'That passkey could not be verified. Try the passkey again.',
            terminal: false,
            retryPasskey: true,
        };
    }
    if (code && serverErrorPresentations[code]) {
        return serverErrorPresentations[code];
    }
    if ((response?.status ?? 0) >= 500) {
        return {
            message: 'The security service is temporarily unavailable. Try again shortly.',
            terminal: false,
        };
    }
    return {
        message: 'That verification did not work. Check the details and try again.',
        terminal: false,
    };
}

function getStringProperty(value: unknown, property: string): string {
    if (!value || typeof value !== 'object') return '';
    const candidate = (value as Record<string, unknown>)[property];
    return typeof candidate === 'string' ? candidate : '';
}
